import React, { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { workflowAction } from "us.collection/actions";
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  BoxIcons,
  IconTypes
} from "us.icons";
import {
  workflowDrawerInitial,
  WorkflowDrawerType,
  ItemMenuActions,
  WORKFLOW_TOOL_DOWNLOAD_URL,
  WorkflowSequenceType,
  WorkflowStateStatus,
  WorkflowViewType
} from "us.collection/components/Workflow/Constants";
import { IStateCategory } from "us.collection/components/Workflow/Interfaces";
import { StateDelete, WorkflowGroup } from "us.collection/repository";
import { IStateList, IWorkflowGroup } from "us.collection/reducers/Workflow/Interfaces";
import { RootState } from "us.helper/types";
import { WorkflowDrawerActionType, URLType } from "us.collection/constants";
import {
  updateSequence,
  isStateListSequenceChanged,
  getAllSelectedWorkflows,
  isAllWorkflowsSelected,
  isStateSelectionEnabled,
  isSelectedStatesExportVisible,
} from "us.collection/components/Workflow/Functions";
import { SaveSequence } from "us.collection/components/Workflow/Repository";
import { useFormikContext } from "formik";
import { getEntityNumberAndType } from "us.collection/components/BMD/Functions";
import { matchPath, useLocation } from "react-router-dom";
import { StateItem } from "./Components";
import { openWorkflowTool } from "us.collection/functions";
import "us.collection/components/Workflow/Home.scss";
import { useDrag, useDrop } from "react-dnd";
import scrollIntoView from "scroll-into-view-if-needed";
import { useDetectClickOutside } from "react-detect-click-outside";
import _ from "lodash";

const { $Popconfirm, $Button, $Tooltip, $Empty, $Checkbox } = Common.Components;

/**
 * @description - The component used in view of workflow category and states
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3055714493/Manage+State+Categories
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 14/11/2022
 */

const StateCategory: React.FC<PropsFromRedux & IStateCategory> = (props) => {
  const { t } = useTranslation();

  const {
    index,
    workflowGroup,
    stateType,
    workflowStates,
    workflowInitStates,
    openDrawer,
    deleteCategory,
    deleteState,
    onMove,
    changeSequence,
    saveSequence,
    exportWorkflows,
    exportWorkflowFiles,
    isDeleting,
    metaData,
    isSequenceSaving,
  } = props;

  const { caseId } = metaData.data ?? {};
  const dragId = "PARENT_ITEM_" + stateType;

  const { values, setFieldValue, setValues } = useFormikContext<any>();
  const { pathname } = useLocation();

  const [popoverOpenId, setPopoverOpenId] = useState(-1);
  const [isDownloadPopupVisible, setDownloadPopupVisible] =
    useState<boolean>(false);

  const dragRef = useRef<any>(null);
  const outsideClickRef = useDetectClickOutside({
    onTriggered: () => {
      values?.selectionEnabledCategory != null &&
        values?.selectedWorkflows?.length > 0 &&
        setValues((previousValues: any) => ({
          ...previousValues,
          selectedWorkflows: [],
          selectionEnabledCategory: null,
        }));
    },
    disableClick:
      values?.selectionEnabledCategory == null ||
      values?.selectionEnabledCategory != workflowGroup?.stateCategoryId,
  });

  const isDraggable = values?.isDraggable && !isSequenceSaving;

  useMemo(() => {
    if (values?.searchText || values?.activeStates != WorkflowStateStatus.ALL) {
      values?.isDraggable && setFieldValue("isDraggable", false);
    }
  }, [values?.searchText, values?.activeStates]);

  // useDrag hook for handle dragging actions
  const [, drag, preview] = useDrag({
    type: dragId,
    item: { index, type: dragId, id: workflowGroup?.stateCategoryId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isDraggable,
  });

  // useDrop hook for handle drop actions
  const [{ canDrop, isOver }, drop] = useDrop({
    accept: dragId,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
    drop(item: any) {
      if (!dragRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      onMove(dragIndex, hoverIndex);

      setTimeout(() => {
        const scrollElement = document.getElementById("ws-tile-" + item?.id);
        if (scrollElement) {
          scrollIntoView(scrollElement, {
            scrollMode: "if-needed",
            block: "start",
            inline: "nearest",
            behavior: "smooth",
          });

          scrollElement.style.backgroundColor = "#f3fff4";
          setTimeout(() => {
            scrollElement.style.backgroundColor = "";
          }, 2000);
        }
      }, 100);

      item.index = hoverIndex;
    },
  });

  drag(preview(dragRef));
  const isDropEnabled = isOver && canDrop;

  const { params }: any = matchPath( {
    path: "/:type/:id?/:module?",
    end: false,
    caseSensitive: false,
  },pathname);

  const entityData = WorkflowGroup.call(
    getEntityNumberAndType(
      params["type"],
      params["id"],
      caseId?.toString() ?? ""
    )
  );

  /**
   * @function
   * @description Get state request payload
   * @returns State request
   */
  const getStateRequest = () => {
    return WorkflowGroup.call(
      getEntityNumberAndType(
        params["type"],
        params["id"],
        caseId?.toString() ?? ""
      )
    );
  };

  /**
   * @function
   * @description Delete workflow category
   * @returns Void
   */
  const deleteWorkflowCategory = () => {
    deleteCategory &&
      deleteCategory({
        stateCategoryId: workflowGroup?.stateCategoryId,
        stateRequest: getStateRequest(),
      });
  };

  /**
   * @function
   * @description Delete workflow state
   * @param id Workflow state id
   * @returns Void
   */
  const deleteWorkflowState = (id: number, categoryId: number) => {
    const deleteStateParams = StateDelete.call({ id, categoryId });
    deleteState &&
      deleteState({
        ...deleteStateParams,
        stateRequest: getStateRequest(),
        searchFilters: _.omit(values, "statesAndEvent"),
      });
    setPopoverOpenId(-1);
  };

  /**
   * Open add edit drawer
   * @param title Drawer title
   * @param type Workflow type (State/Category)
   * @param isEdit Edit view
   * @param stateCategoryId Category id
   * @param stateId State id
   */
  const openWorkflowDrawer = (
    title: string,
    type: string,
    actionType: WorkflowDrawerActionType,
    stateCategoryId?: number,
    stateId?: number,
    workflowGroupStateId?: number
  ) => {
    openDrawer &&
      openDrawer({
        ...workflowDrawerInitial,
        title: title,
        isVisible: true,
        actionType,
        type,
        stateCategoryId,
        stateType,
        stateId,
        workflowGroupStateId,
      });
  };

  /**
   * @description handle states menu actions
   * @param action - action of clicked menu icon
   * @param record - selected record for action
   */
  const handleStateAction = async (
    action: ItemMenuActions,
    record: IStateList
  ) => {
    const {
      id,
      workflowGroupStateId,
      stateGroupId,
      displayName,
      stateGroupName,
    } = record ?? {};
    switch (action) {
      case ItemMenuActions.OPEN_WORKFLOW:
        const socketStatus = await openWorkflowTool({
          ...record,
          ...entityData,
        });
        setDownloadPopupVisible(socketStatus);
        break;
      case ItemMenuActions.DOWNLOAD:
        window.open(WORKFLOW_TOOL_DOWNLOAD_URL, "_self");
        setPopoverOpenId(-1);
        break;
      case ItemMenuActions.EXPORT_WORKFLOW_FILE:
        exportWorkflows &&
          exportWorkflows([
            {
              workflowGroupStateId,
              stateGroupId,
              displayName,
              stateGroupName,
            },
          ]);
        break;
      case ItemMenuActions.EDIT:
        openWorkflowDrawer(
          "US.COLLECTION.CASE:WORKFLOWS.EDIT_STATE",
          WorkflowDrawerType.STATE,
          WorkflowDrawerActionType.EDIT,
          workflowGroup?.stateCategoryId,
          id,
          workflowGroupStateId
        );
        setPopoverOpenId(-1);
        break;
      case ItemMenuActions.COPY:
        openWorkflowDrawer(
          "US.COLLECTION.CASE:WORKFLOWS.COPY_STATE",
          WorkflowDrawerType.STATE,
          WorkflowDrawerActionType.COPY,
          workflowGroup?.stateCategoryId,
          id,
          workflowGroupStateId
        );
        setPopoverOpenId(-1);
        break;
      case ItemMenuActions.DELETE:
        deleteWorkflowState(id, workflowGroup?.stateCategoryId);
        break;
      default:
        break;
    }
  };

  /**
   * @description Update sequence of states
   * @param fromIndex source index
   * @param toIndex target index
   */
  const changeStatesSequence = (fromIndex: number, toIndex: number) => {
    const { updatedStates, updatedCategories } = updateSequence(
      fromIndex,
      toIndex,
      workflowStates.data,
      WorkflowViewType.STATES,
      workflowGroup
    ) as { updatedStates: IStateList[]; updatedCategories: IWorkflowGroup[] };

    const saveParams = SaveSequence.call({
      type: WorkflowSequenceType.STATE,
      categoryId: workflowGroup?.stateCategoryId,
      sequenceList: updatedStates,
    });

    saveSequence && saveSequence({ sequenceList: updatedCategories, saveParams });
  };

  const handleWorkflowSelect = (e: any) => {
    e.stopPropagation();
    setFieldValue("selectionEnabledCategory", workflowGroup?.stateCategoryId);
    setFieldValue(
      "selectedWorkflows",
      getAllSelectedWorkflows(workflowGroup?.stateList, true, "state")
    );
  };

  return (
    <>
      <div
        className="ws-tile"
        ref={drop}
        id={"ws-tile-" + workflowGroup?.stateCategoryId}
      >
        <div
          ref={outsideClickRef}
          className="ws-cat-item"
          style={{
            transform: isDropEnabled ? "translateX(10px)" : "unset",
            opacity: isDropEnabled ? 0.5 : "unset",
          }}
        >
          <div
            className={`ws-cat-item-header ${isDraggable ? "wscih-drag" : ""}`}
            ref={dragRef}
          >
            <div className="d-flex" style={{ overflow: "hidden" }}>
              {workflowGroup?.stateList?.length > 0 &&
                values?.selectionEnabledCategory == workflowGroup?.stateCategoryId && (
                  <$Checkbox
                    name={"category-" + workflowGroup?.stateCategoryId}
                    checked={isAllWorkflowsSelected(values, workflowGroup)}
                    className="mr-2"
                    onChange={(e: any) => {
                      setFieldValue(
                        "selectedWorkflows",
                        getAllSelectedWorkflows(
                          workflowGroup?.stateList,
                          e.target.checked,
                          "state"
                        )
                      );
                    }}
                  />
                )}
              <a
                className="wscih-title"
                onClick={() =>
                  openWorkflowDrawer(
                    "US.COLLECTION.CASE:WORKFLOWS.EDIT_STATE_CATEGORY",
                    WorkflowDrawerType.CATEGORY,
                    WorkflowDrawerActionType.EDIT,
                    workflowGroup?.stateCategoryId
                  )
                }
              >
                <$Tooltip
                  placement="topLeft"
                  title={workflowGroup?.stateCategoryDisplayName}
                >
                  {workflowGroup?.stateCategoryDisplayName}
                </$Tooltip>
              </a>
            </div>
            <div className="wscih-actions">
              {isStateSelectionEnabled(values, workflowGroup) && params["type"] == URLType.BUREAU && (
                <$Button
                  type="link"
                  size="small"
                  className="we-download-btn"
                  data-testid="enable-selection"
                  onClick={(e) => handleWorkflowSelect(e)}
                >
                  {t("US.COMMON:COMMON.SELECT")}
                </$Button>
              )}
              {isSelectedStatesExportVisible(values, workflowGroup) && (
                <$Tooltip
                  placement="top"
                  title={t(
                    "US.COLLECTION.CASE:WORKFLOWS.EXPORT_SELECTED_WORKFLOWS"
                  )}
                >
                  <$Button
                    type="link"
                    size="small"
                    data-testid="download-selection"
                    icon={
                      <BoxIcons
                        type={IconTypes.BOX_ICON}
                        name="export"
                        className="ws-export-icon mb-1"
                      />
                    }
                    onClick={() => exportWorkflowFiles()}
                  />
                </$Tooltip>
              )}
              {workflowGroup?.stateList?.length == 0 && (
                <$Tooltip
                  placement="top"
                  title={t("US.COLLECTION.CASE:WORKFLOWS.DELETE_CATEGORY")}
                >
                  <$Popconfirm
                    title={t(
                      "US.COLLECTION.CASE:WORKFLOWS.DO_YOU_WANT_TO_DELETE_THE_CATEGORY"
                    )}
                    placement="topLeft"
                    onConfirm={() => deleteWorkflowCategory()}
                    okText={t("US.COMMON:COMMON.YES")}
                    cancelText={t("US.COMMON:COMMON.NO")}
                  >
                    <$Button
                      type="link"
                      size="small"
                      className="ml-2"
                      icon={<DeleteOutlined />}
                      danger
                    />
                  </$Popconfirm>
                </$Tooltip>
              )}
              <$Tooltip
                placement="top"
                title={t("US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_STATE")}
              >
                <$Button
                  type="default"
                  size="small"
                  icon={<PlusOutlined />}
                  disabled={isDeleting}
                  onClick={() =>
                    openWorkflowDrawer(
                      "US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_STATE",
                      WorkflowDrawerType.STATE,
                      WorkflowDrawerActionType.ADD,
                      workflowGroup?.stateCategoryId
                    )
                  }
                  className="ml-2"
                />
              </$Tooltip>
            </div>
          </div>
          <div className="ws-cat-item-body">
            {workflowGroup?.stateList?.length == 0 && (
              <$Empty
                className="py-5 m-0"
                description={`${t(
                  "US.COLLECTION.CASE:WORKFLOWS.NO_STATES_ARE_FOUND_UNDER_THIS_WORKFLOW_CATEGORY"
                )}`}
                image={$Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
            {workflowGroup?.stateList?.length > 0 && (
              <>
                {workflowGroup?.stateList?.map(
                  (item: IStateList, index: number) => {
                    return (
                      <StateItem
                        key={index.toString()}
                        stateRecord={item}
                        index={index}
                        popoverOpenId={popoverOpenId}
                        isDownloadPopupVisible={isDownloadPopupVisible}
                        onMove={changeStatesSequence}
                        onChangeStateAction={handleStateAction}
                        onChangePopup={(visibleId) =>
                          setPopoverOpenId(visibleId)
                        }
                        onChangeDownloadPopup={(visible) =>
                          setDownloadPopupVisible(visible)
                        }
                      />
                    );
                  }
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const { category, categoryDeletion, stateDeletion, sequence, exportWorkflow } =
  workflowAction;

const mapStateToProps = (state: RootState) => {
  const { Workflow, domainView } = state;
  const {
    workflowStateDrawer,
    isDeleting,
    workflowStates,
    workflowInitStates,
    isSequenceSaving,
  } = Workflow;
  const { metaData } = domainView;
  return {
    isSequenceSaving,
    workflowInitStates,
    workflowStateDrawer,
    workflowStates,
    isDeleting,
    metaData,
  };
};

const mapDispatchToProps = {
  openDrawer: category.openDrawer,
  deleteCategory: categoryDeletion.delete,
  deleteState: stateDeletion.delete,
  changeSequence: sequence.update,
  saveSequence: sequence.save,
  exportWorkflows: exportWorkflow.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(StateCategory);
